import { useState, useEffect } from "react";
import { getErrorMessages } from "../lib/localizedText";

const useFXForm = (initialFormData, locale = "GB") => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const errorMessages = getErrorMessages(locale);

  // Parse URL parameters and set to form data
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const newFormData = { ...formData };
    newFormData.gclid = params.get("gclid") || "";
    newFormData.utmCampaign = params.get("utm_campaign") || "";
    newFormData.utmChannel = params.get("utm_channel") || "";
    newFormData.utmContent = params.get("utm_content") || "";
    newFormData.utmMedium = params.get("utm_medium") || "";
    newFormData.utmSource = params.get("utm_source") || "";
    newFormData.utmTerm = params.get("utm_term") || "";
    setFormData(newFormData);
  }, []);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = errorMessages.firstName;
    if (!formData.lastName) newErrors.lastName = errorMessages.lastName;
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = errorMessages.email;
    if (!formData.phone) newErrors.phone = errorMessages.phone;
    if (!formData.country) newErrors.country = errorMessages.country;
    if (!formData.state && formData.region === "US")
      newErrors.state = errorMessages.state;
    if (!formData.zip) newErrors.zip = errorMessages.zip;
    if (!formData.flyPrivate) newErrors.flyPrivate = errorMessages.flyPrivate;

    if (
      formData.flyPrivate === "Yes" ||
      formData.flyPrivate === "Oui" ||
      formData.flyPrivate === "Ja"
    ) {
      if (!formData.currentlyTravel)
        newErrors.currentlyTravel = errorMessages.currentlyTravel;
      if (!formData.flyAnnually)
        newErrors.flyAnnually = errorMessages.flyAnnually;
    }

    if (!formData.program) newErrors.program = errorMessages.program;
    if (!formData.aboutUs) newErrors.aboutUs = errorMessages.aboutUs;

    if (!formData.optIn && formData.region === "UK") {
      newErrors.optIn = errorMessages.optIn;
    }

    return newErrors;
  };

  const sanitize = (value) => {
    return typeof value === "string"
      ? value.replace(/<\/?[^>]+(>|$)/g, "")
      : value;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const sanitizedData = {};
    for (const key in formData) {
      sanitizedData[key] = sanitize(formData[key]);
    }

    setIsSubmitting(true);
    setSubmitError(false);

    try {
      const response = await fetch("/wp-json/fxform/v1/submit-form", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(sanitizedData),
      });

      if (response.ok) {
        setIsSubmitting(false);
        setSuccess(true);
        setTimeout(() => {
          // TODO UNCOMMENT IN PRODUCTION WILL NEED TESTING
          let redirectURL;

          if (sanitizedData.region === "US") {
            redirectURL = "/thank-you";
          } else if (sanitizedData.region === "UK") {
            // Check the locale for EU region to determine the specific language thank-you page
            if (locale === "FR") {
              redirectURL = "/fr/merci-beaucoup";
            } else if (locale === "DE") {
              redirectURL = "/de/vielen-dank";
            } else {
              // Default to English EU thank-you page if locale is not specified
              redirectURL = "/eu/thank-you";
            }
          }

          window.location.href = redirectURL;
        }, 1000);
      } else {
        setSubmitError(true);
        setIsSubmitting(false);
      }
    } catch (error) {
      setSubmitError(true);
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    errors,
    isSubmitting,
    success,
    submitError,
    handleChange,
    handleSubmit,
  };
};

export default useFXForm;
